import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  Users,
  Settings,
  Database,
  Palette,
  Bell,
  Lock,
  ChevronRight,
  Key,
  FileText,
  Globe,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface SettingsCategory {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  iconBg: string;
  subOptions: string[];
  available: boolean;
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const settingsCategories: SettingsCategory[] = [
    {
      title: 'Security & Permissions',
      description: 'Manage roles, permissions, and access controls',
      icon: Shield,
      path: '/settings/permissions',
      iconBg: 'bg-red-500',
      subOptions: [
        'Role Definitions',
        'Feature Access Controls',
        'Data Visibility Rules',
        'Action Permissions',
        'Field-Level Security',
      ],
      available: true,
    },
    {
      title: 'Organization & Users',
      description: 'Manage organization settings and user accounts',
      icon: Users,
      path: '/settings/organization',
      iconBg: 'bg-blue-500',
      subOptions: [
        'User Management',
        'Team Structure',
        'Department Settings',
        'Invite Members',
        'Access Logs',
      ],
      available: false,
    },
    {
      title: 'System Configuration',
      description: 'Configure system-wide settings and preferences',
      icon: Settings,
      path: '/settings/system',
      iconBg: 'bg-purple-500',
      subOptions: [
        'Judges Sheet Templates',
        'API Configuration',
        'Database Settings',
        'Cache Management',
        'Debug Options',
      ],
      available: true,
    },
    {
      title: 'Data Management',
      description: 'Control data retention, backups, and migrations',
      icon: Database,
      path: '/settings/data',
      iconBg: 'bg-green-500',
      subOptions: [
        'Backup Schedule',
        'Data Retention',
        'Export Options',
        'Import Tools',
        'Migration Settings',
      ],
      available: false,
    },
    {
      title: 'Appearance & Theme',
      description: 'Customize the look and feel of the application',
      icon: Palette,
      path: '/settings/appearance',
      iconBg: 'bg-pink-500',
      subOptions: [
        'Theme Selection',
        'Color Schemes',
        'Typography',
        'Logo & Branding',
        'Layout Options',
      ],
      available: false,
    },
    {
      title: 'Notifications',
      description: 'Configure notification preferences and channels',
      icon: Bell,
      path: '/settings/notifications',
      iconBg: 'bg-yellow-500',
      subOptions: [
        'Email Settings',
        'Push Notifications',
        'In-App Alerts',
        'Notification Rules',
        'Digest Preferences',
      ],
      available: false,
    },
    {
      title: 'Authentication',
      description: 'Manage authentication methods and security',
      icon: Lock,
      path: '/settings/authentication',
      iconBg: 'bg-indigo-500',
      subOptions: [
        'Login Methods',
        'Two-Factor Auth',
        'SSO Configuration',
        'Password Policies',
        'Session Management',
      ],
      available: false,
    },
    {
      title: 'API & Integrations',
      description: 'Manage API keys and third-party integrations',
      icon: Key,
      path: '/settings/api',
      iconBg: 'bg-orange-500',
      subOptions: [
        'API Keys',
        'Webhooks',
        'OAuth Apps',
        'Connected Services',
        'Rate Limits',
      ],
      available: false,
    },
    {
      title: 'Compliance & Legal',
      description: 'Manage compliance settings and legal documents',
      icon: FileText,
      path: '/settings/compliance',
      iconBg: 'bg-gray-500',
      subOptions: [
        'Privacy Policy',
        'Terms of Service',
        'GDPR Settings',
        'Data Processing',
        'Audit Logs',
      ],
      available: false,
    },
    {
      title: 'Localization',
      description: 'Configure language and regional settings',
      icon: Globe,
      path: '/settings/localization',
      iconBg: 'bg-teal-500',
      subOptions: [
        'Language Settings',
        'Date & Time Formats',
        'Currency Display',
        'Number Formats',
        'Timezone Configuration',
      ],
      available: false,
    },
  ];

  const handleCardClick = (category: SettingsCategory) => {
    if (category.available) {
      navigate(category.path);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">Settings</h1>
        <p className="mt-2 text-muted-foreground">
          Manage your application configuration and preferences
        </p>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {settingsCategories.map((category) => {
          const IconComponent = category.icon;

          return (
            <Card
              key={category.path}
              className={`relative ${
                category.available
                  ? 'hover:shadow-lg transition-shadow cursor-pointer group'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => handleCardClick(category)}
            >
              {/* Coming Soon Badge */}
              {!category.available && (
                <div className="absolute top-4 right-4 z-10">
                  <Badge variant="secondary" className="text-xs">
                    Coming Soon
                  </Badge>
                </div>
              )}

              {/* Card Header */}
              <CardHeader className="pb-3">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${category.iconBg} text-white shrink-0`}>
                    <IconComponent className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground">
                      {category.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {category.description}
                    </p>
                  </div>
                  {category.available && (
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                  )}
                </div>
              </CardHeader>

              {/* Card Content */}
              <CardContent>
                <div className="space-y-2 mb-4">
                  {category.subOptions.map((option, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-muted-foreground"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                      <span>{option}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={category.available ? 'outline' : 'secondary'}
                  className="w-full"
                  disabled={!category.available}
                  tabIndex={category.available ? 0 : -1}
                >
                  {category.available ? 'Configure Settings' : 'Coming Soon'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Footer Note */}
      <Card className="mt-8 border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <h4 className="font-medium text-foreground">
                Security & Permissions Available
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                The Security & Permissions module is now available. Configure
                role-based access control, manage permissions, and set up
                field-level security for your application.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
